// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type { ExperienceFormData } from '../types/schemas';

const EXPERIENCES_QUERY_KEY = ['experiences'] as const;
const CREDITS_QUERY_KEY = ['credits'] as const;

/** Hook for fetching all work experiences. */
export function useExperiences() {
  return useQuery({
    queryKey: EXPERIENCES_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/v1/experiences');

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
  });
}

/** Hook for fetching a single work experience by ID. */
export function useExperience(id: string) {
  return useQuery({
    queryKey: [...EXPERIENCES_QUERY_KEY, id],
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        '/api/v1/experiences/{id}',
        { params: { path: { id } } },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    enabled: !!id,
  });
}

/** Hook for creating a new work experience. */
export function useCreateExperience() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ExperienceFormData) => {
      const { data, error } = await apiClient.POST('/api/v1/experiences', {
        body: {
          job_title: params.job_title,
          company_name: params.company_name,
          location: params.location || undefined,
          start_date: params.start_date,
          end_date: params.end_date || undefined,
          description: params.description || undefined,
        },
      });

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXPERIENCES_QUERY_KEY });
    },
  });
}

/** Hook for updating an existing work experience. */
export function useUpdateExperience() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...params
    }: ExperienceFormData & { id: string }) => {
      const { data, error } = await apiClient.PUT(
        '/api/v1/experiences/{id}',
        {
          params: { path: { id } },
          body: {
            job_title: params.job_title,
            company_name: params.company_name,
            location: params.location || undefined,
            start_date: params.start_date,
            end_date: params.end_date || undefined,
            description: params.description || undefined,
          },
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXPERIENCES_QUERY_KEY });
    },
  });
}

/** Hook for deleting a work experience. */
export function useDeleteExperience() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await apiClient.DELETE(
        '/api/v1/experiences/{id}',
        { params: { path: { id } } },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXPERIENCES_QUERY_KEY });
    },
  });
}

/** Hook for bulk deleting work experiences. */
export function useBulkDeleteExperiences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/experiences/bulk-delete',
        { body: { ids } },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXPERIENCES_QUERY_KEY });
    },
  });
}

/** Hook for AI-polishing a work experience description.
 *  Supports two modes:
 *  - Edit mode: pass `id` of a saved experience.
 *  - Create mode: pass `description` to polish inline text.
 *    A placeholder ID is sent since the route requires one, but the backend ignores it.
 *  When `includeDetailLines` is true, the experience's bullet-point detail lines are
 *  included as additional context for the AI to generate a richer description. */
export function usePolishExperience() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      jobTitle,
      description,
      includeDetailLines,
    }: {
      id?: string;
      jobTitle?: string;
      description?: string;
      includeDetailLines?: boolean;
    }) => {
      const pathId =
        id || '00000000-0000-0000-0000-000000000000';
      const body: Record<string, string | boolean> = {};
      if (jobTitle) body.job_title = jobTitle;
      if (description) body.description = description;
      if (includeDetailLines) body.include_detail_lines = true;

      const { data, error } = await apiClient.POST(
        '/api/v1/experiences/{id}/polish',
        {
          params: { path: { id: pathId } },
          body,
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
    },
  });
}



/** Hook for reordering work experiences. */
export function useReorderExperiences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (experienceIds: string[]) => {
      const { data, error } = await apiClient.PUT(
        '/api/v1/experiences/reorder',
        {
          body: { experience_ids: experienceIds },
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(EXPERIENCES_QUERY_KEY, data);
    },
  });
}
